import React, { useState, useEffect } from 'react';
import { Button, Card, CardBody, Col, Form, FormGroup, Label, Row } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { createStructuredSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

// styles
import '../style.scss';
// actions
import { useHistory } from 'react-router-dom';
import { SMKLinks } from 'utils/links';
import { collegeClearCache } from 'containers/college/collegeDetailsHelper';
import { getCollegesInfraList, updateCollegeFacilityDetails } from '../../actions';
// selectors
import { selectCollegesInfraList, collegeDetailSelector } from '../../selectors';
// components
import TextEditor from '../../../../components/TextEditor';
import ToggleButton from '../../../../components/ToggleButton';
import useRequest from '../../../../hooks/useRequest';
import Loader from '../../../../components/Loader';
import { userSelector } from '../../../../common/globalComponents/selectors';

const stateSelector = createStructuredSelector({
  collegesInfraList: selectCollegesInfraList,
  collegeDetail: collegeDetailSelector,
  user: userSelector,
});

const FacilityInfraDetails = () => {
  const { user, collegesInfraList, collegeDetail } = useSelector(stateSelector);

  const dispatch = useDispatch();
  const history = useHistory();
  const [categoryWiseInfraList, setCategoryWiseInfraList] = useState({});

  useEffect(() => {
    dispatch(getCollegesInfraList.trigger());
  }, []);

  useEffect(() => {
    if (!isEmpty(collegesInfraList)) {
      const infraList = {};
      collegesInfraList.forEach((i) => {
        if (i.infraType) {
          if (!infraList[i.infraType.name]) {
            infraList[i.infraType.name] = [];
          }
          infraList[i.infraType.name].push(i);
        }
      });
      setCategoryWiseInfraList(infraList);
    }
  }, [collegesInfraList]);

  const [updateFacilityDetailsRequest, { loading: updateFacilityDetailsLoading }] = useRequest({
    url: '/college/admin/edit/updateFacilityDetails',
    method: 'POST',
    onSuccess: (data) => {
      collegeClearCache({
        urlSlug: collegeDetail?.slug,
        collegeId: collegeDetail?.id,
      });

      window.location.reload();
    },
  });

  const formik = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      selectedInfraList: {},
      facilityDetails: collegeDetail.facilityDetails?.facilityDetails,
      safetyDetails: collegeDetail.facilityDetails?.safetyDetails,
    },
    onSubmit(values) {
      const { selectedInfraList, facilityDetails, safetyDetails } = values;

      let selectedArray = [];
      Object.values(selectedInfraList).forEach((array) => {
        selectedArray = [...selectedArray, ...array];
      });

      updateFacilityDetailsRequest({
        data: {
          collegeId: collegeDetail.id,
          facilityDetails,
          safetyDetails,
          selectedInfraList: selectedArray,
        },
      });
    },
    validationSchema: Yup.object().shape({
      selectedInfraList: Yup.object(),
      facilityDetails: Yup.string().nullable(),
      safetyDetails: Yup.string().nullable(),
    }),
  });

  useEffect(() => {
    // dispatch(getCollegesInfraList.trigger());
    const { infra } = collegeDetail;
    if (!isEmpty(infra)) {
      const selectedInfra = {};
      infra.forEach((i) => {
        if (i.infraType) {
          if (!selectedInfra[i.infraType.name]) {
            selectedInfra[i.infraType.name] = [];
          }
          selectedInfra[i.infraType.name].push(i);
        }
      });
      formik.setFieldValue('selectedInfraList', selectedInfra);
    }
  }, []);

  const handleToggleFacility = (checked, category, facility) => {
    let array = formik.values.selectedInfraList[category] || [];
    if (checked) {
      array = [...array, facility];
    } else {
      array = array.filter((f) => f.id !== facility.id);
    }
    formik.setFieldValue('selectedInfraList', {
      ...formik.values.selectedInfraList,
      [category]: array,
    });
  };

  const renderFacilities = () => {
    if (!isEmpty(categoryWiseInfraList)) {
      return Object.entries(categoryWiseInfraList).map(([category, facilities]) => (
        <Card key={category}>
          <CardBody>
            <div className="d-flex justify-content-between mb-4 border_bottom">
              <h5 className="font-size-14 ">{category} Facilities</h5>
              <span>{`(${formik.values.selectedInfraList[category]?.length || 0}/${facilities.length})`}</span>
            </div>

            <Row>
              {facilities?.map((facility) => {
                return (
                  <Col sm={3} key={`fac_${facility.id}`}>
                    <ToggleButton
                      label={facility.name}
                      checked={
                        !isEmpty(formik.values.selectedInfraList[category]) &&
                        formik.values.selectedInfraList[category].some((f) => facility.id === f.id)
                      }
                      onChange={(e) => handleToggleFacility(e.target.checked, category, facility)}
                    />
                  </Col>
                );
              })}
            </Row>
          </CardBody>
        </Card>
      ));
    }
    return null;
  };

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Loader isActive={updateFacilityDetailsLoading} />

      <h5 className="mb-4">Facilities and Infrastructure Details</h5>

      {renderFacilities()}

      <Card>
        <CardBody>
          <FormGroup className="mb-4">
            <Label htmlFor="collegedesc" className="form-label  ">
              Facility Details
            </Label>

            <TextEditor
              simple
              initialValue={formik.values.facilityDetails}
              onChange={(val) => formik.setFieldValue('facilityDetails', val)}
            />
            {formik.touched.facilityDetails && (
              <div className="invalid-feedback d-block">{formik.errors.facilityDetails}</div>
            )}
          </FormGroup>

          <FormGroup className="mb-4">
            <Label htmlFor="collegedesc" className="form-label  ">
              Safety Details
            </Label>
            <TextEditor
              simple
              initialValue={formik.values.safetyDetails}
              onChange={(val) => formik.setFieldValue('safetyDetails', val)}
            />
            {formik.touched.safetyDetails && (
              <div className="invalid-feedback d-block">{formik.errors.safetyDetails}</div>
            )}
          </FormGroup>
        </CardBody>
      </Card>

      {(user?.entityPermissions?.college.admin ||
        (collegeDetail.editable &&
          (collegeDetail?.createdBy?.id === user.id || collegeDetail?.createdBy?.teamLead?.id === user.id))) && (
        <div className="mb-4">
          <Button type="submit" color="primary" className="me-3 ">
            Update Details
          </Button>
          <a
            href="#"
            className="ms-4 text-dark"
            onClick={() => {
              history.replace(SMKLinks.COLLEGE_LISTING);
            }}>
            Cancel
          </a>
        </div>
      )}
    </Form>
  );
};

export default FacilityInfraDetails;
